import { isScmSignInPurpose } from "./signin.helpers"

import type { ScmRequester } from "@/api/useScmSignIn.types"
import type { PendingScmSignIn, ScmSession } from "./scm-session.helpers.types"

// SCM sign-in state lives in sessionStorage: per tab, gone when the tab closes, never sent with a request.
const PENDING_KEY = "kodem_scm_signin_pending"
const ATTEMPTS_KEY = "kodem_scm_signin_attempts"
const sessionKey = (purposeRef: string) => `kodem_scm_token_${purposeRef}`
const linkSlugKey = (purposeRef: string) => `kodem_scm_link_slug_${purposeRef}`

// A token this close to expiry is treated as gone, so a request never starts with a dying token.
const EXPIRY_MARGIN_MS = 30_000
// Two automatic redirects in this window means something loops: stop and show a button instead.
const LOOP_WINDOW_MS = 2 * 60_000
const MAX_AUTOMATIC_ATTEMPTS = 2

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)

const isString = (value: unknown): value is string => typeof value === "string"

const isPendingScmSignIn = (value: unknown): value is PendingScmSignIn =>
  isRecord(value) &&
  isString(value.codeVerifier) &&
  isString(value.purposeRef) &&
  isString(value.slug) &&
  isString(value.purpose) &&
  isScmSignInPurpose(value.purpose)

const isScmRequester = (value: unknown): value is ScmRequester =>
  isRecord(value) &&
  isString(value.login) &&
  isString(value.name) &&
  isString(value.email)

const isScmSession = (value: unknown): value is ScmSession =>
  isRecord(value) &&
  isString(value.token) &&
  typeof value.expiresAt === "number" &&
  isString(value.slug) &&
  isScmRequester(value.requester)

const isAttempts = (value: unknown): value is Record<string, number[]> =>
  isRecord(value) &&
  Object.values(value).every(
    (attempts) =>
      Array.isArray(attempts) && attempts.every((at) => typeof at === "number")
  )

// Storage another tab or an older build wrote may not match; it then counts as missing.
const readJson = <T>(key: string, isValid: (value: unknown) => value is T) => {
  try {
    const raw = sessionStorage.getItem(key)
    const value: unknown = raw ? JSON.parse(raw) : null

    return isValid(value) ? value : null
  } catch {
    return null
  }
}

export const savePendingScmSignIn = (pending: PendingScmSignIn) =>
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending))

export const readPendingScmSignIn = () =>
  readJson(PENDING_KEY, isPendingScmSignIn)

export const clearPendingScmSignIn = () =>
  sessionStorage.removeItem(PENDING_KEY)

export const saveScmSession = (purposeRef: string, session: ScmSession) =>
  sessionStorage.setItem(sessionKey(purposeRef), JSON.stringify(session))

export const readScmSession = (purposeRef: string) => {
  const session = readJson(sessionKey(purposeRef), isScmSession)

  if (!session || session.expiresAt * 1000 - EXPIRY_MARGIN_MS < Date.now()) {
    return null
  }

  return session
}

// The slug outlives the token, so an expired session can still sign in again without the link.
export const readScmSessionSlug = (purposeRef: string) =>
  readJson(sessionKey(purposeRef), isScmSession)?.slug || ""

// The PR link's tenant, kept when a Kodem session drops it from the URL; that session may turn out expired.
// Every Kodem user's link passes here, so blocked storage must never throw.
export const saveScmLinkSlug = (purposeRef: string, slug: string) => {
  try {
    if (slug) sessionStorage.setItem(linkSlugKey(purposeRef), slug)
  } catch {
    return
  }
}

export const readScmLinkSlug = (purposeRef: string) => {
  try {
    return sessionStorage.getItem(linkSlugKey(purposeRef)) || ""
  } catch {
    return ""
  }
}

// Keeps the slug so the page can sign in again; only the token stops counting.
export const expireScmSession = (purposeRef: string) => {
  const session = readJson(sessionKey(purposeRef), isScmSession)

  if (session) saveScmSession(purposeRef, { ...session, expiresAt: 0 })
}

// Records an automatic redirect to the SCM; false when it would be one too many.
export const allowAutomaticScmSignIn = (purposeRef: string) => {
  const now = Date.now()
  const allAttempts = readJson(ATTEMPTS_KEY, isAttempts) || {}
  const attempts = (allAttempts[purposeRef] || []).filter(
    (at) => now - at < LOOP_WINDOW_MS
  )

  if (attempts.length >= MAX_AUTOMATIC_ATTEMPTS) return false

  sessionStorage.setItem(
    ATTEMPTS_KEY,
    JSON.stringify({ ...allAttempts, [purposeRef]: [...attempts, now] })
  )

  return true
}

export const resetScmSignInAttempts = () =>
  sessionStorage.removeItem(ATTEMPTS_KEY)
